import { useEffect, useState } from 'react'
import { Dialog, Flex, Text, Button, ScrollArea, IconButton, Tooltip, Box } from '@radix-ui/themes'
import { FileText, Eye, X, Loader2, Trash2 } from 'lucide-react'
import axios from 'axios'
import { getToken } from '@/utils/utils'
import { makeUrlTransferPreview, makeUrlTransferDocuments } from '@/hooks/connectionData'
import { TransferDocuments } from '@/interfaces/Transfer'

interface DocumentSlot {
  key: keyof TransferDocuments
  label: string
}

const DOCUMENT_SLOTS: DocumentSlot[] = [
  { key: 'frontBuyerId', label: 'Cédula comprador (frontal)' },
  { key: 'backBuyerId', label: 'Cédula comprador (reverso)' },
  { key: 'frontSellerId', label: 'Cédula vendedor (frontal)' },
  { key: 'backSellerId', label: 'Cédula vendedor (reverso)' },
  { key: 'draftContract', label: 'Borrador contrato (PDF)' },
  { key: 'notarizedContract', label: 'Contrato notariado' },
  { key: 'circulationPermit', label: 'Permiso de circulación' },
  { key: 'powerDocument', label: 'Poder notarial' },
]

interface TransferDocumentPreviewProps {
  transferId: string
  organizationId: string
  documents: TransferDocuments
  onUpdate: () => void
  sellerType?: 'Natural' | 'Legal'
}

export const TransferDocumentPreview = ({
  transferId,
  organizationId,
  documents = {},
  onUpdate,
  sellerType = 'Natural',
}: TransferDocumentPreviewProps) => {
  const [open, setOpen] = useState(false)
  const [urls, setUrls] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [selectedDoc, setSelectedDoc] = useState<keyof TransferDocuments | null>(null)

  // Ensure documents is not null (default param only handles undefined)
  const safeDocuments = documents || {}

  // Filter slots to only show uploaded documents and map labels based on seller type
  const availableSlots = DOCUMENT_SLOTS.filter((slot) => safeDocuments[slot.key]).map((slot) => {
    let updatedSlot = { ...slot }
    if (sellerType === 'Legal') {
      if (slot.key === 'powerDocument') {
        updatedSlot.label = 'Documento Legal'
      }
      if (slot.key === 'frontSellerId') {
        updatedSlot.label = 'E-RUT Empresa (Frontal)'
      }
      if (slot.key === 'backSellerId') {
        updatedSlot.label = 'RUT Representante Legal (frontal)'
      }
    }
    return updatedSlot
  })

  if (sellerType === 'Legal') {
    const backSellerIdIndex = availableSlots.findIndex((s) => s.key === 'backSellerId')
    // We only add the slot to availableSlots if the document actually exists in safeDocuments
    if (safeDocuments['backSellerRepId'] && availableSlots.findIndex((s) => s.key === 'backSellerRepId') === -1) {
       // Insert it after backSellerId if it exists, otherwise just push it
       if (backSellerIdIndex !== -1) {
         availableSlots.splice(backSellerIdIndex + 1, 0, {
           key: 'backSellerRepId',
           label: 'Cédula Representante Legal (reverso)'
         } as any)
       } else {
         availableSlots.push({
           key: 'backSellerRepId',
           label: 'Cédula Representante Legal (reverso)'
         } as any)
       }
    }
  }


  useEffect(() => {
    if (open && availableSlots.length > 0) {
      fetchUrls()
      // Select the first available document by default if none selected
      // Or if selected doc was deleted, select another one
      if (!selectedDoc || !safeDocuments[selectedDoc]) {
        setSelectedDoc(availableSlots[0].key)
      }
    } else if (open && availableSlots.length === 0) {
      // If all documents are deleted while open
      setOpen(false)
    }
  }, [open, safeDocuments]) // React to documents changes

  const fetchUrls = async () => {
    setLoading(true)
    try {
      const token = getToken()
      const response = await axios.get(makeUrlTransferPreview(transferId), {
        headers: { Authorization: `Bearer ${token}` },
      })
      setUrls(response.data)
    } catch (error) {
      console.error('Error fetching preview URLs:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (e: React.MouseEvent, documentType: keyof TransferDocuments) => {
    e.stopPropagation() // Prevent selecting the document when clicking delete
    if (!window.confirm('¿Eliminar este documento?')) return

    try {
      const token = getToken()
      const apiUrl = makeUrlTransferDocuments()

      await axios.delete(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
        data: { transferId, documentType, organizationId },
      })
      onUpdate()
    } catch (error) {
      console.error('Error deleting document:', error)
      alert('Error al eliminar documento')
    }
  }

  const handleDeleteAll = async () => {
    if (!window.confirm('¿Eliminar TODOS los documentos?')) return

    try {
      const token = getToken()
      const apiUrl = `${makeUrlTransferDocuments()}/all`

      await axios.delete(apiUrl, {
        headers: { Authorization: `Bearer ${token}` },
        data: { transferId, organizationId },
      })
      onUpdate()
      setOpen(false) // Close dialog as there are no docs left
    } catch (error) {
      console.error('Error deleting all documents:', error)
      alert('Error al eliminar documentos')
    }
  }

  const currentUrl = selectedDoc ? urls[selectedDoc] : null

  const renderPreview = () => {
    if (loading) {
      return (
        <Flex align="center" justify="center" className="h-full w-full">
          <Loader2 className="animate-spin w-8 h-8 text-gray-400" />
        </Flex>
      )
    }

    if (!currentUrl) {
      return (
        <Flex align="center" justify="center" className="h-full w-full bg-gray-100 rounded-md">
          <Text color="gray">Seleccione un documento para visualizar</Text>
        </Flex>
      )
    }

    return (
      <object data={currentUrl} type="application/pdf" className="w-full h-full rounded-md border">
        <iframe src={currentUrl} className="w-full h-full rounded-md border" title="Preview" />
      </object>
    )
  }

  if (availableSlots.length === 0) return null

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger>
        <Button variant="soft" size="2">
          <Eye className="w-4 h-4 mr-1" />
          Ver documentos
        </Button>
      </Dialog.Trigger>

      <Dialog.Content style={{ maxWidth: '90vw', height: '90vh', padding: 0 }}>
        <Flex direction="column" className="h-full">
          {/* Header */}
          <Flex justify="between" align="center" className="p-4 border-b">
            <Text size="4" weight="bold">
              Vista previa de documentos
            </Text>
            <Dialog.Close>
              <IconButton variant="ghost" color="gray">
                <X className="w-5 h-5" />
              </IconButton>
            </Dialog.Close>
          </Flex>

          {/* Body */}
          <Flex className="flex-1 overflow-hidden">
            {/* Left: Main Preview */}
            <Flex className="flex-1 p-4 bg-gray-50 overflow-hidden relative">{renderPreview()}</Flex>

            {/* Right: Sidebar List */}
            <Flex direction="column" className="w-72 border-l bg-white flex-shrink-0">
              <Text size="2" weight="bold" className="p-3 border-b bg-gray-50">
                Documentos disponibles
              </Text>

              <ScrollArea type="auto" scrollbars="vertical" className="flex-1">
                <Flex direction="column">
                  {availableSlots.map((slot) => (
                    <button
                      key={slot.key}
                      onClick={() => setSelectedDoc(slot.key)}
                      className={`p-3 text-left hover:bg-gray-50 border-b border-gray-100 transition-colors group ${
                        selectedDoc === slot.key ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                      }`}
                    >
                      <Flex align="center" justify="between" width="100%">
                        <Flex align="center" gap="2" className="flex-1 overflow-hidden">
                          <FileText
                            className={`w-4 h-4 flex-shrink-0 ${selectedDoc === slot.key ? 'text-blue-500' : 'text-gray-400'}`}
                          />
                          <Text size="2" weight={selectedDoc === slot.key ? 'medium' : 'regular'} className="truncate">
                            {slot.label}
                          </Text>
                        </Flex>

                        <Tooltip content="Eliminar">
                          <IconButton
                            size="1"
                            variant="ghost"
                            color="red"
                            className="opacity-0 group-hover:opacity-100 transition-opacity ml-2 flex-shrink-0"
                            onClick={(e) => handleDelete(e, slot.key)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </IconButton>
                        </Tooltip>
                      </Flex>
                    </button>
                  ))}
                </Flex>
              </ScrollArea>

              {/* Sidebar Footer */}
              <Box p="3" className="border-t bg-gray-50">
                <Button color="red" variant="soft" className="w-full" onClick={handleDeleteAll}>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Eliminar todos
                </Button>
              </Box>
            </Flex>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}
